<template>
    <div class="page__content availabilities-by-month">
        <div class="columns is-multiline">
            <div class="column is-12">
                <h2 class="page__headline">{{ $t('availabilities.detail.headline') }}</h2>
                <div class="backlink">
                    <router-link :to="{ name: 'availabilities-redirect', params: {lang: lang} }" class="link">
                        <span @click="onBacklinkClicked()">
                            <span class="pre">&laquo;</span> <span class="main">{{ $t('availabilities.detail.back') }}</span>
                        </span>
                    </router-link>
                </div>
            </div>
            <div class="column is-12">
                <div class="notification is-yellow" v-if="!readOnly">{{ $t('availabilities.intro.copy3') }}</div>
                <div class="notification is-yellow" v-else>{{ $t('availabilities.intro.copy3ReadOnly') }}</div>
            </div>
        </div>

        <div class="columns is-multiline">
            <div class="column is-5 month-chooser">
                <router-link :to="{ name: 'availabilities-by-month', params: previousMonthParams }" class="button" :class="{'is-loading': loading}" v-if="!isPast(previousMonth)">
                    <span @click="onPreviousNextMonthClicked(-1)">&lt</span>
                </router-link>
                <h3 class="month-name">{{ $t('generic.calendar.monthnames.long')[month - 1] }} {{ year }}</h3>
                <router-link :to="{ name: 'availabilities-by-month', params: nextMonthParams }" class="button" :class="{'is-loading': loading}">
                    <span @click="onPreviousNextMonthClicked(1)">&gt</span>
                </router-link>
            </div>
            <div class="column is-7 availability-filter">
                <availability-percent-filter v-model="filter" classes="is-pulled-right" trackName="AvailabilitiesByMonth"></availability-percent-filter>
            </div>
        </div>
        <transition-group name="filter-list" tag="div" class="columns is-multiline campsites">
            <div v-for="availability of availabilitiesByCampsite" class="column is-4 filter-list-item" v-if="shouldDisplay(availability.percent)" :key="availability.id">
                <router-link :to="{ name: 'availabilities-by-campsite', params: { campsiteId: availability.id, lang: lang }, query: { date_from: dateFrom, date_until: dateUntil } }" class="box availability-box active" :class="{'is-loading': loading}" v-if="!isPast(dateUntil)">
                    <span @click="onCampsiteClicked()">
                        <div class="month-name">{{ availability.name }}</div>
                        <battery v-model="availability.percent" v-if="!loading"></battery>
                    </span>
                </router-link>
                <span class="box availability-box inactive" :class="{'is-loading': loading}" v-else>
                    <span @click="onDisabledCampsiteClicked()">
                        <div class="month-name">{{ availability.name }}</div>
                        <battery v-model="availability.percent" v-if="!loading"></battery>
                    </span>
                </span>
            </div>
        </transition-group>
    </div>
</template>

<script>
    import { Api } from '../../../api';
    import { Tracking } from '../../../utils';
    import { AppModel } from '../../../models';
    import { Battery, AvailabilityPercentFilter } from '../../components';
    import moment from 'moment';

    const getStartAndEndDates = function(year, month) {
        const start = moment(`${year}-${month}-1`, 'YYYY-M-D');
        const end = moment(start).add(1, 'months').subtract(1, 'days');
        return [
            start.format('YYYY-MM-DD'),
            end.format('YYYY-MM-DD'),
        ];
    };

    export default {
        name: 'availabilities-by-month',

        components: {
            Battery,
            AvailabilityPercentFilter,
        },

        props: {
            year: {
                type: Number,
                'default': null,
            },
            month: {
                type: Number,
                'default': null,
            },
        },

        data() {
            return {
                readOnly: true,
                campsites: null,
                availabilitiesByCampsite: [],
                loading: true,
                numDays: 0,
                filter: [true, true, true, true],
            };
        },

        beforeRouteEnter(to, from, next) {
            const [start, end] = getStartAndEndDates(to.params.year, to.params.month);

            Api.bootstrap().then(function(response) {
                return Promise.all([response, Api.availabilities.fetchAvailabilities({
                    'date_from': start,
                    'date_until': end,
                }), Api.campsites.fetchCampsites()]);
            }).then(function(response) {
                next((vm) => {
                    vm.readOnly = AppModel.activeCampground.settings.pms_api_active;
                    vm.numDays = moment(end, 'YYYY-MM-DD').diff(moment(start, 'YYYY-MM-DD'), 'days') + 1;
                    vm.setAvailabilitiesByCampsite(response[1].body, response[2].body);
                    vm.loading = false;
                });
            });
        },

        beforeRouteUpdate(to, from, next) {
            this.loading = true;
            const [start, end] = getStartAndEndDates(to.params.year, to.params.month);
            Api.availabilities.fetchAvailabilities({
                'date_from': start,
                'date_until': end,
            }).then((response) => {
                next();
                this.numDays = moment(end, 'YYYY-MM-DD').diff(moment(start, 'YYYY-MM-DD'), 'days') + 1;
                this.setAvailabilitiesByCampsite(response.body);
                this.loading = false;
            });
        },

        computed: {
            dateFrom() {
                return getStartAndEndDates(this.year, this.month)[0];
            },
            dateUntil() {
                return getStartAndEndDates(this.year, this.month)[1];
            },
            previousMonthParams() {
                let m = this.month - 1;
                let y = this.year;
                if(m < 1) {
                    m = 12;
                    y -= 1;
                }
                return { year: y, month: m, lang: this.lang };
            },
            nextMonthParams() {
                let m = this.month + 1;
                let y = this.year;
                if(m > 12) {
                    m = 1;
                    y += 1;
                }
                return { year: y, month: m, lang: this.lang };
            },
            previousMonth() {
                const p = this.previousMonthParams;
                return new Date(p.year, p.month, 0);
            },
            lang() {
                return AppModel.lang;
            },
        },

        methods: {
            setAvailabilitiesByCampsite(availabilities, campsites = null) {
                if(campsites === null || campsites === undefined) {
                    if(this.campsites === null || this.campsites === undefined) {
                        console.warn('Called setAvailabilitiesByCampsite without campsites beeing set');
                        return;
                    }
                    campsites = this.campsites;
                } else {
                    this.campsites = campsites;
                }
                this.availabilitiesByCampsite = [];

                for(let campsite of campsites) {
                    let availability = {
                        id: campsite.id,
                        name: campsite.name,
                    };
                    let sumQuantity = 0;
                    let sumAvailability = 0;
                    for(let avail of availabilities) {
                        if(avail.campsite === campsite.id) {
                            sumQuantity += avail.quantity;
                            if(avail.availability !== null && avail.availability !== undefined && avail.availability < avail.quantity) {
                                sumAvailability += Math.max(0, avail.availability - avail.bookings);
                            } else {
                                sumAvailability += Math.max(0, avail.quantity - avail.bookings);
                            }
                        }
                    }
                    if(sumQuantity > 0) {
                        availability.percent = Math.round(sumAvailability * 10000.0 / sumQuantity) / 100.0;
                    } else {
                        availability.percent = 0;
                    }
                    this.availabilitiesByCampsite.push(availability);
                }
            },

            shouldDisplay(percent) {
                let idx = 0;
                if(percent <= 50 && percent > 20) {
                    idx = 1;
                } else if(percent <= 20 && percent > 0) {
                    idx = 2;
                } else if(percent === 0) {
                    idx = 3;
                }
                return this.filter[idx];
            },

            isPast(dt) {
                let m = moment(dt);
                m.add(1, 'days');
                return m.toDate().getTime() < new Date().getTime();
            },

            onBacklinkClicked() {
                Tracking.trackEvent('AvailabilitiesByMonth', 'backLinkClicked');
            },

            onPreviousNextMonthClicked(direction) {
                const v = direction === -1 ? 'previous' : 'next';
                Tracking.trackEvent('AvailabilitiesByMonth', `${v}MonthClicked`);
            },

            onCampsiteClicked() {
                Tracking.trackEvent('AvailabilitiesByMonth', 'campsiteClicked');
            },
            onDisabledCampsiteClicked() {
                Tracking.trackEvent('AvailabilitiesByMonth', 'disabledCampsiteClicked');
            },
        },
    };
</script>

<style lang="scss">
    @import './availabilities-by-month.scss';
</style>



// WEBPACK FOOTER //
// src/views/pages/availabilities/availabilities-by-month.vue